import React, { useState, useMemo } from 'react';
import { Globe, AlertTriangle } from 'lucide-react';
import { Button } from '../../ui/Button';
import Select from 'react-select';

interface Country {
  code: string;
  name: string;
}

interface GeolocationSettingsProps {
  geolocationSettings: {
    enabled: boolean;
    mode: string;
    countries: string[];
    availableCountries: Country[];
  };
  isGeolocationSettingsSaving: boolean;
  onGeolocationSettingsChange: (field: string, value: any) => void;
  onSave: () => void;
}

export const GeolocationSettings: React.FC<GeolocationSettingsProps> = ({
  geolocationSettings,
  isGeolocationSettingsSaving,
  onGeolocationSettingsChange,
  onSave,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Convert available countries to react-select format
  const countryOptions = useMemo(() => {
    return geolocationSettings.availableCountries.map((country) => ({
      value: country.code,
      label: `${country.name} (${country.code})`,
    }));
  }, [geolocationSettings.availableCountries]);

  // Get selected countries
  const selectedCountries = useMemo(() => {
    return countryOptions.filter((option) =>
      geolocationSettings.countries.includes(option.value)
    );
  }, [countryOptions, geolocationSettings.countries]);

  // Filter countries based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return countryOptions;
    const term = searchTerm.toLowerCase();
    return countryOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.value.toLowerCase().includes(term)
    );
  }, [countryOptions, searchTerm]);

  const handleCountryChange = (selectedOptions: any) => {
    const selectedCodes = selectedOptions
      ? selectedOptions.map((option: any) => option.value)
      : [];
    onGeolocationSettingsChange('countries', selectedCodes);
  };

  const customSelectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: 'var(--color-bg-primary)',
      borderColor: state.isFocused ? 'var(--color-accent)' : 'var(--color-border)',
      color: 'var(--color-text-primary)',
      '&:hover': {
        borderColor: 'var(--color-accent)',
      },
      boxShadow: state.isFocused ? '0 0 0 1px var(--color-accent)' : 'none',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--color-bg-primary)',
      borderColor: 'var(--color-border)',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'var(--color-accent)'
        : state.isFocused
        ? 'var(--color-bg-secondary)'
        : 'var(--color-bg-primary)',
      color: state.isSelected
        ? 'white'
        : 'var(--color-text-primary)',
      '&:hover': {
        backgroundColor: state.isSelected
          ? 'var(--color-accent)'
          : 'var(--color-bg-secondary)',
      },
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--color-accent)',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: 'white',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: 'white',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        color: 'white',
      },
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-primary)',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-secondary)',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-primary)',
    }),
  };

  return (
    <div>
      <div className="mb-6">
        <h4 className="text-lg font-medium flex items-center" style={{ color: 'var(--color-text-primary)' }}>
          <Globe className="w-5 h-5 mr-2" style={{ color: 'var(--color-accent)' }} />
          Geolocation Restrictions
        </h4>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Restrict access based on geographic location (country)
        </p>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* Enable Geolocation Restrictions */}
        <div className="relative flex items-center justify-between p-4 border rounded-lg" style={{ 
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: 'var(--color-border)'
        }}>
          <div>
            <h6 className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              Enable Geolocation Restrictions
            </h6>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Block or allow access based on user's country location
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={geolocationSettings.enabled}
              onChange={(e) => onGeolocationSettingsChange('enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {geolocationSettings.enabled && (
          <>
            {/* Restriction Mode */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Restriction Mode
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                {geolocationSettings.mode === 'ALLOW'
                  ? 'Allow only selected countries (whitelist)'
                  : 'Block selected countries (blacklist)'}
              </p>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="restrictionMode"
                    value="ALLOW"
                    checked={geolocationSettings.mode === 'ALLOW'}
                    onChange={(e) => onGeolocationSettingsChange('mode', e.target.value)}
                    className="mr-2"
                    style={{ accentColor: 'var(--color-accent)' }}
                  />
                  <span style={{ color: 'var(--color-text-primary)' }}>Allow (Whitelist)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="restrictionMode"
                    value="BLOCK"
                    checked={geolocationSettings.mode === 'BLOCK'}
                    onChange={(e) => onGeolocationSettingsChange('mode', e.target.value)}
                    className="mr-2"
                    style={{ accentColor: 'var(--color-accent)' }}
                  />
                  <span style={{ color: 'var(--color-text-primary)' }}>Block (Blacklist)</span>
                </label>
              </div>
            </div>

            {/* Country Selection */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                {geolocationSettings.mode === 'ALLOW' ? 'Allowed Countries' : 'Blocked Countries'}
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                Select countries to {geolocationSettings.mode === 'ALLOW' ? 'allow' : 'block'} access from
              </p>
              <Select
                isMulti
                options={filteredOptions}
                value={selectedCountries}
                onChange={handleCountryChange}
                onInputChange={setSearchTerm}
                placeholder={`Search and select countries...`}
                styles={customSelectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {geolocationSettings.countries.length > 0 && (
                <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {geolocationSettings.countries.length} countr{geolocationSettings.countries.length === 1 ? 'y' : 'ies'} selected
                </p>
              )}
            </div>

            {/* Warning Message */}
            <div className="p-4 border rounded-lg flex items-start" style={{ 
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-warning)',
            }}>
              <AlertTriangle className="w-5 h-5 mr-3 mt-0.5" style={{ color: 'var(--color-warning)' }} />
              <div>
                <h6 className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Important Security Notice
                </h6>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {geolocationSettings.mode === 'ALLOW'
                    ? 'Only users from the selected countries will be able to access the application. All other countries will be blocked.'
                    : 'Users from the selected countries will be blocked from accessing the application. All other countries will be allowed.'}
                  {' '}Geolocation is determined by IP address and may not be 100% accurate.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Save Button - Always visible */}
        <div className="flex justify-end space-x-3 mt-8">
          <Button
            variant="primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSave();
            }}
            disabled={isGeolocationSettingsSaving}
            size="sm"
            type="button"
          >
            {isGeolocationSettingsSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

